import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@lexcelon/react-util';
import { DogPicture, CatPicture, HorsePicture, GoatPicture, CattlePicture, Instrument, LIGHTEST_GREEN } from '@parasightsysteminc/companion-react';
import { Grid, ToggleButton, ToggleButtonGroup, Typography, Autocomplete, MenuItem, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { Redirect } from 'react-router-dom';
import { VeterinarianDialog, LabTechDialog, AnimalDialog } from '../../components';
import { Block as BlockIcon, PriorityHigh as PriorityHighIcon, QuestionMark as QuestionMarkIcon } from '@mui/icons-material';
import { formatString } from '../../util';
import cookies from '../../cookies';
import SafetyImage from '../../assets/images/safety.png';
import { styled } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

//APIs
import { listVeterinarians, listLabTechs, listAnimals, createTest, listTestTypes, getUser } from '../../api';

import { setError, setSuccess } from '../../alerts';
import { INVENTORY_TYPE_STATUSES } from '@parasightsysteminc/companion-react';
import { closeConfirm, confirm, startConfirmLoading, stopConfirmLoading } from '../../alerts/confirm';

// Constants
const ANIMAL_AGES = [
  'Less than 1 year old',
  '1 or more years old'
];

const StyledToggleButton = styled(ToggleButton)(({ backgroundColor = LIGHTEST_GREEN, borderWidth = '4px' }) => ({
  backgroundColor: backgroundColor,
  borderWidth,
  '&.Mui-selected': {
    backgroundColor: backgroundColor, // Change background color to red when selected
    '&:hover': {
      backgroundColor: backgroundColor, // Change background color to green when hovered
    },
  },
  '&:hover': {
    backgroundColor: backgroundColor, // Change background color to green when hovered
  },
}));

class NewTestForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      testTypes: [],
      veterinarian: null,
      labTech: null,
      animal: null,
      veterinarians: [],
      labTechs: [],
      animals: [],
      inventoryTypes: [],
      testType: null,
      veterinarianPerformingTest: false,
      isLoading: false,
      loadListsFailed: false,
      animalAge: '',
      sampleName: '',
      comments: '',
      animalSpeciesId: 1,
      redirectToHome: false,
      patientNameText: '',
      /** @type {import("@parasightsysteminc/companion-react").User | null} */
      user: null
    };
  }

  loadLists = () => {
    getUser().then(user => {
      this.setState({ user });

      //Load Veterinarians
      listVeterinarians().then(({ results }) => {
        let selectedVeterinarian = results.length === 1 ? results[0] : null;
        results.map(veterinarian => {
          if (veterinarian.getId() === user.getLastUsedVeterinarianId()) selectedVeterinarian = veterinarian;
        });
        this.setState({ veterinarians: results, veterinarian: selectedVeterinarian });
      }).catch(error => { //Catch veterinarian error
        setError(error ?? 'Error: Unable to retrieve veterinarians. Please try again.');
        this.setState({ loadListsFailed: true });
      });

      //Load LabTechs
      listLabTechs().then(({ results }) => {
        let selectedLabTech = null;
        results.map(labTech => {
          if (labTech.getId() === user.getLastUsedLabTechId()) selectedLabTech = labTech;
        });
        this.setState({ labTechs: results, labTech: selectedLabTech, veterinarianPerformingTest: selectedLabTech == null });
      }).catch(error => { //Catch lab tech error
        setError(error ?? 'Error: Unable to retrieve lab techs. Please try again.');
        this.setState({ loadListsFailed: true });
      });
    }).catch(error => {
      setError(error ?? 'Error: Could not retrieve user object.');
    });

    //Load Animals
    listAnimals().then(({ results }) => {
      this.setState({ animals: results });
    }).catch(error => { //Catch animal error
      setError(error ?? 'Error: Unable to retrieve animals. Please try again.');
      this.setState({ loadListsFailed: true });
    });

  }

  componentDidMount() {
    this.loadLists();
  }

  onSubmit = (e) => {
    e.preventDefault();

    // Ensure a test type was selected
    if (this.state.testType?.getId() == null) {
      setError('Error: Please select a test type.');
      return;
    }

    // Give a warning if in training mode
    if (this.state.user?.getPractice()?.getIsDemo() && this.state.user?.getPractice()?.getIdentifier() !== 'PTEST1') {
      confirm({
        title: 'WARNING: Test in Training Mode',
        body: 'Training mode is for demonstrative purposes ONLY. Tests performed in training mode use an abbreviated cycle with results replicated from previous tests. The results from this test will not be valid with the fecal sample you provide. To turn off training mode, go to your account settings. Are you sure you want to continue?',
        onConfirm: () => {
          this.createTest();
        },
        danger: true
      });
    }
    else if (this.state.animalSpeciesId >= 3 /* Pasture Animal */) {
      confirm({
        title: 'Attention: Please follow the pasture preparation procedure before running a test.',
        body: 'Are you ready to continue?',
        onConfirm: () => {
          this.createTest();
        },
        danger: true
      });
    }
    else this.createTest();
  }

  createTest = () => {
    // Start loading
    this.setState({ isLoading: true });
    this.props.onLoading(true);
    startConfirmLoading();

    if (this.state.veterinarianPerformingTest) {
      this.setState({ labTech: null });
    }

    createTest({
      veterinarianId: this.state.veterinarian.getId(),
      labTechId: this.state.labTech?.getId() ?? null,
      animalId: this.state.animal.getId(),
      testTypeId: this.state.testType.getId(),
      // This check is probably not needed, but it's here just in case
      instrumentSerialNumber: this.props.instrument instanceof Instrument ? this.props.instrument.getSerialNumber() : undefined,
      animalAge: this.state.animalAge,
      sampleName: this.state.sampleName !== '' ? this.state.sampleName : null,
      comments: this.state.comments !== '' ? this.state.comments : null
    }).then((test) => {
      setSuccess('Successfully created test!');
      this.setState({ isLoading: false });
      this.props.onLoading(false);
      stopConfirmLoading();
      closeConfirm();
      this.props.onSuccess(test);
    }).catch((error) => {
      setError(error ?? 'Error: Unable to create test. Please try again.');
      this.setState({ isLoading: false });
      stopConfirmLoading();
      closeConfirm();
      this.props.onLoading(false);
    });
  }

  onChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  }

  onVeterinarianChange = (_, newVeterinarian) => {
    this.setState({ veterinarian: newVeterinarian });
  }

  onLabTechChange = (_, newLabTech) => {
    this.setState({ labTech: newLabTech });
  }

  onAnimalChange = (_, newAnimal) => {
    this.updateTestTypes(newAnimal);
  }

  // This is only used to autofill the name field if user opts to Create New Patient
  // For actual patient selection, see onAnimalChange above
  onPatientNameTextChange = (e) => {
    this.setState({ patientNameText: e.target.value });
  }

  onTestTypeChange = (_, newTestType) => {
    this.setState({ testType: newTestType });
  }

  onNewVeterinarianSuccess = (newVeterinarian) => {
    this.state.veterinarians.push(newVeterinarian);
    this.setState({ veterinarian: newVeterinarian });
  }

  onNewLabTechSuccess = (newLabTech) => {
    this.state.labTechs.push(newLabTech);
    this.setState({ labTech: newLabTech });
  }

  onNewAnimalSuccess = (newAnimal) => {
    this.state.animals.push(newAnimal);
    this.updateTestTypes(newAnimal);
  }

  updateTestTypes = (newAnimal) => {
    if (newAnimal == null) {
      this.setState({ testTypes: [], testType: null, animal: null, animalSpeciesId: null, animalAge: '' });
      return;
    }

    listTestTypes({ options: { where: { animalSpeciesId: newAnimal.getAnimalSpeciesId() } } }).then(({ results }) => {

      // Update each test type's associated inventory type statuses
      results.forEach(testType => {
        testType.getInventoryTypes()?.forEach(inventoryType => {
          let associatedInventoryType = this.props.cartridgeInventoryTypes.find(cartridgeInventoryType => cartridgeInventoryType.getId() === inventoryType.getId());
          inventoryType.setStatus(associatedInventoryType?.getStatus() ?? INVENTORY_TYPE_STATUSES.UNKNOWN);
        });

        testType.getInventoryTypes().sort((a, b) => a.getZIndex() - b.getZIndex());
      });

      let testType = results.find(testType => testType.getId() === 1 || testType.getId() === 6) ?? (results.length > 0 && !results[0].getIsComingSoon() ? results[0] : null);

      // Check to ensure all cartridges are ready
      testType?.getInventoryTypes()?.forEach(inventoryType => {
        if (inventoryType.getIsCartridge() && !(inventoryType.isGood() || inventoryType.isLow())) {
          testType = null;
        }
      });

      this.setState({ testTypes: results, testType, animal: newAnimal, animalSpeciesId: newAnimal?.getAnimalSpeciesId(), animalAge: newAnimal != null ? (newAnimal.getAge() ?? '') : this.state.animalAge });
    }).catch(error => {
      setError(error ?? 'Error: Unable to retrieve test types. Please try again.');
    });
  }

  toggleButtonStyle = ({ disabled, numItems = null }) => ({ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', opacity: disabled ? 0.4 : 1, cursor: disabled ? 'auto' : 'pointer', width: numItems != null ? `${Math.floor(100/numItems)}%` : 'auto' });

  render() {
    if (this.state.redirectToHome) return <Redirect to='/' />;
    return this.state.loadListsFailed ? <Redirect to='/tests/' /> : (

      <div>
        <Typography variant='h2' style={{ marginBottom: '0.5em' }}>Practitioners Information</Typography>
        <Grid container spacing={0} direction='row-reverse'>
          <Grid item xs={1} md={3}>
            <div style={{ height: '3.5em' }}>
              <VeterinarianDialog onSuccess={this.onNewVeterinarianSuccess} disabled={this.state.isLoading} />
            </div>
            <div style={{ height: '4em' }}></div>

            {!this.state.veterinarianPerformingTest && (
              <div style={{ height: '3.5em', marginBottom: '0.5em' }}>
                <LabTechDialog onSuccess={this.onNewLabTechSuccess} disabled={this.state.isLoading} />
              </div>
            )}

            <div style={{ height: '3.5em' }}>
              <AnimalDialog onSuccess={this.onNewAnimalSuccess} animalName={this.state.patientNameText} disabled={this.state.isLoading} />
            </div>
          </Grid>
        </Grid>

        <form onSubmit={this.onSubmit} >
          <Grid container style={{ marginTop: this.state.veterinarianPerformingTest ? ('-11em') : ('-15em') }} spacing={0} direction='row'>
            <Grid item xs={11} md={9}>
              {/* Veterinarian - Select one with Autocomplete */}
              <Autocomplete
                disablePortal
                name="veterinarian"
                required
                options={this.state.veterinarians}
                getOptionLabel={(option) => `${option?.getFirstName()} ${option?.getLastName()}, ${option?.getTitle()}`}
                style={{ marginRight: '0.5em', marginBottom: '0.5em' }}
                disabled={this.state.isLoading}
                value={this.state.veterinarian}
                onChange={this.onVeterinarianChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required={true}
                    label='Veterinarian'
                    variant='filled'
                    disabled={this.state.isLoading}
                  />
                )}
              />

              <div style={{ height: '3.5em', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormControlLabel style={{ marginBottom: '0.5em' }}
                  control={<Checkbox checked={this.state.veterinarianPerformingTest} />}
                  onChange={() => this.setState({ veterinarianPerformingTest: !this.state.veterinarianPerformingTest })}
                  name='veterinarianPerformingTest'
                  label='Veterinarian is performing test' />
              </div>

              {!this.state.veterinarianPerformingTest && (
                <>
                  {/* Lab Tech - Select one with Autocomplete */}
                  <Autocomplete
                    disablePortal
                    name="labTech"
                    options={this.state.labTechs}
                    getOptionLabel={(option) => `${option?.getFirstName()} ${option?.getLastName()}, ${option?.getTitle()}`}
                    style={{ marginRight: '0.5em', marginBottom: '0.5em' }}
                    disabled={this.state.isLoading}
                    value={this.state.labTech}
                    onChange={this.onLabTechChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label='Lab Tech'
                        variant='filled'
                        disabled={this.state.isLoading}
                      />
                    )}
                  />
                </>
              )}

              {/* Animal - Select one with Autocomplete */}
              <Autocomplete
                disablePortal
                name="animal"
                options={this.state.animals}
                getOptionLabel={(option) => `${option?.getName()} (${option?.getOwnerFirstName()} ${option?.getOwnerLastName()}) - ${option?.getAnimalSpecies()?.getName()}`}
                style={{ marginRight: '0.5em' }}
                disabled={this.state.isLoading}
                value={this.state.animal}
                onChange={this.onAnimalChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label='Patient'
                    variant='filled'
                    onChange={this.onPatientNameTextChange}
                    disabled={this.state.isLoading}
                  />
                )}
              />

            </Grid>
          </Grid>

          <TextField
            select
            required
            name='animalAge'
            label='Patient Age'
            fullWidth
            style={{ marginTop: '0.5em' }}
            value={this.state.animalAge}
            onChange={this.onChange}
            variant='filled'
            disabled={this.state.isLoading}>
            {ANIMAL_AGES.map((option, index) => {
              return (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </TextField>

          <Typography variant='h2' style={{ marginBottom: '0.5em', marginTop: '1em' }}>Test Information</Typography>

          {/* Animal Species Selector */}
          {this.state.animal != null &&
          <>
            <Typography variant='button' style={{ color: 'slategrey' }}>Animal Species*</Typography>
            <ToggleButtonGroup
              value={this.state.animalSpeciesId === 6 ? 5 : this.state.animalSpeciesId /* Goat and sheep will use same image for now */}
              color='primary'
              exclusive
              style={{ marginBottom: '20px' }}
            >
              <ToggleButton value={1} disabled={this.state.animalSpeciesId !== 1} style={this.toggleButtonStyle({ disabled: this.state.animalSpeciesId !== 1, numItems: 5 })}>
                <img style={{ width: '50%', height: '90%', objectFit: 'contain' }} src={DogPicture} />
                <Typography variant='body1' style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 14, color: '#454545' }}>Dog</Typography>
              </ToggleButton>
              <ToggleButton value={2} disabled={this.state.animalSpeciesId !== 2} style={this.toggleButtonStyle({ disabled: this.state.animalSpeciesId !== 2, numItems: 5 })}>
                <img style={{ width: '50%', height: '90%', objectFit: 'contain' }} src={CatPicture} />
                <Typography variant='body1' style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 14, color: '#454545' }}>Cat</Typography>
              </ToggleButton>
              <ToggleButton value={3} disabled={this.state.animalSpeciesId !== 3} style={this.toggleButtonStyle({ disabled: this.state.animalSpeciesId !== 3, numItems: 5 })}>
                <img style={{ width: '50%', height: '90%', objectFit: 'contain' }} src={HorsePicture} />
                <Typography variant='body1' style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 14, color: '#454545' }}>Horse</Typography>
              </ToggleButton>
              <ToggleButton value={4} disabled={this.state.animalSpeciesId !== 3} style={this.toggleButtonStyle({ disabled: this.state.animalSpeciesId !== 4, numItems: 5 })}>
                <img style={{ width: '50%', height: '90%', objectFit: 'contain' }} src={CattlePicture} />
                <Typography variant='body1' style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 14, color: '#454545' }}>Cattle</Typography>
              </ToggleButton>
              <ToggleButton value={5} disabled={this.state.animalSpeciesId !== 3} style={this.toggleButtonStyle({ disabled: this.state.animalSpeciesId !== 5 && this.state.animalSpeciesId !== 6, numItems: 5 })}>
                <img style={{ width: '50%', height: '90%', objectFit: 'contain' }} src={GoatPicture} />
                <Typography variant='body1' style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 14, color: '#454545' }}>Sheep/Goat</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </>}

          {/* Test Type - Select one with Autocomplete */}
          <Typography variant='button' style={{ color: 'slategrey' }}>Test Type*</Typography>
          {this.state.user?.getPractice()?.getIsDemo() && <Typography variant='body1' style={{ color: 'red', fontWeight: 'bold', marginBottom: '0.5em' }}>IN TRAINING MODE: CAPTURED IMAGES USED ARE FROM PREVIOUS SAMPLES</Typography>}
          {this.state.animal == null && <Typography variant='body1'>Please select a patient to view test type options.</Typography>}
          {this.state.animal != null && this.state.testTypes?.length === 0 && <Typography variant='body1'>No test types available for this animal species.</Typography>}
          <ToggleButtonGroup
            color='primary' 
            value={this.state.testType}
            exclusive
            onChange={(_, testType) => {
              if (testType == null) return;
              this.setState({ testType });
            }}
            style={{ marginBottom: '10px' }}
            fullWidth
            orientation='vertical'
            disabled={this.state.isLoading}
          >
            {this.state.testTypes?.map((testType, index) => {

              // Check to ensure all cartridges are ready
              let cartridgesAreReady = true;
              testType.getInventoryTypes()?.forEach(inventoryType => {
                if (inventoryType.getIsCartridge() && !(inventoryType.isGood() || inventoryType.isLow())) {
                  cartridgesAreReady = false;
                }
              });

              const colorHex = testType.getInventoryTypes()?.find(inventoryType => inventoryType.getIsTestKit())?.getColorHex();

              return (
                <StyledToggleButton backgroundColor={colorHex != null ? '#' + colorHex : null} key={index} value={testType} style={{ ...this.toggleButtonStyle({ disabled: !cartridgesAreReady || testType.getIsComingSoon() }) }} disabled={!cartridgesAreReady || testType.getIsComingSoon()}>
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', textDecoration: this.state.testType?.getId() === testType.getId() ? 'underline' : '', padding: '5px 5px 5px 0px' }}>
                    <div style={{ width: '50px', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backroundColor: 'pink' }}>
                      {this.state.testType?.getId() === testType.getId() ? <CheckCircleOutlineIcon style={{ color: '#454545', width: '40px', height: '40px' }} /> : <RadioButtonUncheckedIcon style={{ color: '#454545', width: '40px', height: '40px' }} />}
                    </div>

                    <div style={{ flexGrow: 1 }}>
                      <Typography variant='body1' style={{ textAlign: 'left', fontWeight: 'bold', fontSize: 14, color: '#454545' }}>{testType?.getName()}</Typography>
                      {testType.getIsComingSoon() && (
                        <Typography variant='body1' style={{ textAlign: 'left', fontSize: 12, color: 'red' }}>Coming Soon</Typography>
                      )}

                      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                        {!testType.getIsComingSoon() && testType.getInventoryTypes()?.filter(inventoryType => !inventoryType.isGood() && inventoryType.getIsCartridge())?.map((inventoryType, index) => {
                          let widthPercentage = testType.getInventoryTypes()?.length != 0 ? Math.floor(100.0 / testType.getInventoryTypes().length + 0.5) : 0;

                          return (
                            <div key={index} style={{ width: widthPercentage + '%', maxWidth: '100px', margin: '1%' }}>
                              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                                <img src={inventoryType.getImageUrl()} style={{ width: '100%', objectFit: 'contain'/*, opacity: inventoryType.getIsCartridge() && !(inventoryType.isGood() || inventoryType.isLow()) ? '40%' : '100%'*/ }} />

                                {/* For showing empty cartridge */}
                                {inventoryType.getIsCartridge() &&
                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  {!(inventoryType.isGood() || inventoryType.isLow() || inventoryType.isUnknown()) && <BlockIcon style={{ color: 'red', width: '100%', height: '100%' }} />}
                                  {inventoryType.isLow() && <PriorityHighIcon style={{ color: 'goldenrod', width: '100%', height: '100%' }} />}
                                  {inventoryType.isUnknown() && <QuestionMarkIcon style={{ color: 'grey', width: '100%', height: '100%' }} />}
                                </div>}
                              </div>

                              <Typography variant='button' style={{ fontWeight: 'bold', color: '#454545' }}>{formatString(inventoryType.getStatus())}</Typography>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </StyledToggleButton>
              );
            })}
          </ToggleButtonGroup>

          {/* Conditional Replace Cartridge Button */}
          {this.state.testTypes?.some(testType => !testType.getIsComingSoon() && testType.getInventoryTypes()?.some(inventoryType => inventoryType.getIsCartridge() && !(inventoryType.isGood() || inventoryType.isLow()))) && (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px' }}>
              <Button secondary href={`/instruments/${cookies.getInstrumentSerialNumber()}/status`}>Replace Cartridges</Button>
            </div>
          )}

          <TextField
            name='sampleName'
            label='Sample Name (Optional)'
            fullWidth
            style={{ marginTop: '0.5em' }}
            value={this.state.sampleName}
            onChange={this.onChange}
            variant='filled'
            disabled={this.state.isLoading} />

          <TextField
            name='comments'
            label='Comments (Optional)'
            fullWidth
            multiline
            rows={2}
            style={{ marginTop: '0.5em' }}
            value={this.state.comments}
            onChange={this.onChange}
            variant='filled'
            disabled={this.state.isLoading} />

          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', alignItems: 'center', marginTop: '40px' }}>
            {/* Submit Button */}
            <Button type='submit' isLoading={this.state.isLoading} style={{ width: '60%', height: '90px' }}>
              Start Test
            </Button>
          </div>

          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: '15px', marginTop: '40px' }}>
            <img src={SafetyImage} style={{ width: '500px' }} />
          </div>
        </form>
      </div>
    );
  }
}

NewTestForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  test: PropTypes.object,
  onClose: PropTypes.func,
  cartridgeInventoryTypes: PropTypes.arrayOf(PropTypes.object),
  instrument: PropTypes.object.isRequired,
  onLoading: PropTypes.func.isRequired
};

export default NewTestForm;
